import { useState, useMemo, useCallback } from 'react';
import { Plus, RefreshCw, Layers, PackageOpen, AlertTriangle, Loader2 } from 'lucide-react';
import { useGroups, useModal } from './hooks/useGroups';
import StatsCards from './components/StatsCards';
import SearchBar from './components/SearchBar';
import GroupTable from './components/GroupTable';
import CreateGroupModal from './components/CreateGroupModal';
import EditGroupModal from './components/EditGroupModal';
import UserPanel from './components/UserPanel';
import InstrumentPanel from './components/InstrumentPanel';
import GroupDetailPanel from './components/GroupDetailPanel';
import './css/base.css';
import './css/buttons.css';
import './css/stats-cards.css';
import './css/search-bar.css';
import './css/table.css';
import './css/detail-panel.css';
import './css/modals.css';
import './css/subpanels.css';
import './css/forms.css';
import './css/responsive.css';

// Simple toast-like notification
function notify(message, type = 'success') {
  const el = document.createElement('div');
  el.className = `gm-toast gm-toast-${type} animate-slide-down`;
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => {
    el.style.opacity = '0';
    el.style.transform = 'translateY(-10px)';
    setTimeout(() => el.remove(), 300);
  }, 3000);
}

export default function GroupManagement() {
  const { groups, loading, error, stats, fetchAllGroups, deleteGroup, isDefault } = useGroups();
  const { modalState, openModal, closeModal } = useModal();

  // Local UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  // Filter and sort groups
  const filteredGroups = useMemo(() => {
    let result = [...groups];

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(
        (g) =>
          g.name?.toLowerCase().includes(lower) ||
          g.description?.toLowerCase().includes(lower)
      );
    }

    if (filterStatus !== 'all') {
      result = result.filter((g) =>
        filterStatus === 'active' ? g.isActive !== false : g.isActive === false
      );
    }

    switch (sortBy) {
      case 'name':
        result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'users':
        result.sort((a, b) => (b.userCount || 0) - (a.userCount || 0));
        break;
      case 'commission':
        result.sort((a, b) => (b.commission || 0) - (a.commission || 0));
        break;
      case 'created':
        result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      default:
        break;
    }

    return result;
  }, [groups, searchTerm, filterStatus, sortBy]);

  // Handlers
  const handleCreateSuccess = useCallback((result) => {
    fetchAllGroups();
    closeModal('create');
    notify('Group created successfully!');
  }, [fetchAllGroups, closeModal]);

  const handleEditSuccess = useCallback((result) => {
    fetchAllGroups();
    closeModal('edit');
    notify('Group updated successfully!');
  }, [fetchAllGroups, closeModal]);

  const handleUserSuccess = useCallback((message) => {
    fetchAllGroups();
    notify(message || 'Users updated!');
  }, [fetchAllGroups]);

  const handleInstrumentSuccess = useCallback((message) => {
    fetchAllGroups();
    notify(message || 'Instruments updated!');
  }, [fetchAllGroups]);

  const handleDelete = useCallback(async (groupId, groupName) => {
    if (!window.confirm(`Delete group "${groupName}"?\n\nThis action cannot be undone.`)) return;
    try {
      await deleteGroup(groupId);
      notify('Group deleted successfully!');
    } catch (err) {
      notify(err.message, 'error');
    }
  }, [deleteGroup]);

  // Loading state
  if (loading) {
    return (
      <div className="gm-loading">
        <div className="gm-loading-content">
          <div className="gm-spinner" />
          <p className="gm-loading-text">Loading group data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="gm-error">
        <div className="gm-error-content">
          <AlertTriangle className="gm-error-icon" />
          <h2 className="gm-error-title">Error Loading Data</h2>
          <p className="gm-error-msg">{error}</p>
          <button onClick={fetchAllGroups} className="gm-btn-primary">
            <RefreshCw size={16} /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="gm-container">
      {/* Page Header */}
      <div className="gm-header">
        <div>
          <h1 className="gm-title">
            <Layers className="gm-title-icon" />
            Group Management
          </h1>
          <p className="gm-subtitle">
            Manage trading groups, configure leverage settings, and assign users
          </p>
        </div>
        <div className="gm-header-actions">
          <button onClick={fetchAllGroups} className="gm-btn-secondary">
            <RefreshCw size={16} /> Refresh
          </button>
          <button onClick={() => openModal('create', true)} className="gm-btn-primary">
            <Plus size={16} /> Create Group
          </button>
        </div>
      </div>

      {/* Statistics */}
      <StatsCards stats={stats} />

      {/* Main Content */}
      <div className="gm-main-card">
        {/* Toolbar */}
        <div className="gm-card-header">
          <h2 className="gm-card-title">
            Trading Groups
            <span className="gm-card-title-count">
              {filteredGroups.length} of {groups.length} groups
            </span>
          </h2>
        </div>

        <div style={{ padding: '0 20px 12px' }}>
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filterStatus={filterStatus}
            onFilterChange={setFilterStatus}
            sortBy={sortBy}
            onSortChange={setSortBy}
          />
        </div>

        {/* Table or Empty */}
        {filteredGroups.length > 0 ? (
          <GroupTable
            groups={filteredGroups}
            isDefault={isDefault}
            onView={(id) => openModal('detail', id)}
            onEdit={(id) => openModal('edit', id)}
            onUsers={(id, name) => openModal('users', { groupId: id, groupName: name })}
            onInstruments={(id, name) => openModal('instruments', { groupId: id, groupName: name })}
            onDelete={handleDelete}
          />
        ) : (
          <div className="gm-empty">
            <PackageOpen className="gm-empty-icon" />
            <h3 className="gm-empty-title">No Groups Found</h3>
            <p className="gm-empty-msg">
              {searchTerm || filterStatus !== 'all'
                ? 'No groups match your current filters. Try adjusting your search criteria.'
                : 'There are no trading groups yet. Create your first group to get started.'}
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <button onClick={() => openModal('create', true)} className="gm-btn-primary">
                <Plus size={16} /> Create First Group
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Modals & Panels ── */}
      {modalState.create && (
        <CreateGroupModal
          onSuccess={handleCreateSuccess}
          onClose={() => closeModal('create')}
        />
      )}

      {modalState.edit && (
        <EditGroupModal
          groupId={modalState.edit}
          onSuccess={handleEditSuccess}
          onClose={() => closeModal('edit')}
        />
      )}

      {modalState.users && (
        <UserPanel
          groupId={modalState.users.groupId}
          groupName={modalState.users.groupName}
          onSuccess={handleUserSuccess}
          onClose={() => closeModal('users')}
        />
      )}

      {modalState.instruments && (
        <InstrumentPanel
          groupId={modalState.instruments.groupId}
          groupName={modalState.instruments.groupName}
          onSuccess={handleInstrumentSuccess}
          onClose={() => closeModal('instruments')}
        />
      )}

      {modalState.detail && (
        <GroupDetailPanel
          groupId={modalState.detail}
          onClose={() => closeModal('detail')}
          onEdit={(id) => { closeModal('detail'); openModal('edit', id); }}
          onUsers={(id, name) => { closeModal('detail'); openModal('users', { groupId: id, groupName: name }); }}
          onInstruments={(id, name) => { closeModal('detail'); openModal('instruments', { groupId: id, groupName: name }); }}
        />
      )}
    </div>
  );
}
